import { create } from 'zustand';

export type Papel = 'Surgeon' | 'ConsultantRemote' | 'HospitalAdmin' | 'GlobalAdmin';
export type TipoCliente = 'Hospital' | 'Clinica' | 'ProfissionalAutonomo';

/** Acesso a um cliente (hospital, clínica ou profissional autônomo), com o papel naquele cliente. */
export type Vinculo = {
  hospitalId: string;
  nome: string;
  tipo: TipoCliente;
  papel: Exclude<Papel, 'GlobalAdmin'>;
};

/**
 * `papel` e `hospitalId` são os do cliente ATIVO da sessão — o mesmo cirurgião é HospitalAdmin
 * no consultório dele e Surgeon no hospital. `vinculos` lista todos os clientes acessíveis.
 */
export type AuthUser = {
  id: string;
  email: string;
  nome: string;
  papel: Papel;
  hospitalId: string | null;
  vinculos: Vinculo[];
  /** A conta tem verificação em duas etapas ligada. */
  doisFAAtivo: boolean;
  /** O papel no cliente ativo exige 2FA (administradores). */
  doisFAObrigatorio: boolean;
  /** Esta sessão entrou com o segundo fator. */
  sessaoComMfa: boolean;
  /** Senha definida no convite: trocar é obrigatório antes de usar o sistema. */
  deveTrocarSenha: boolean;
};

type TokenPayload = {
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
};

const REFRESH_KEY = 'vyxora.refreshToken';

function readRefresh(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(REFRESH_KEY);
}

function writeRefresh(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(REFRESH_KEY, token);
  else window.localStorage.removeItem(REFRESH_KEY);
}

type AuthState = {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  setUser: (user: AuthUser | null) => void;
  setTokens: (t: TokenPayload) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  refreshToken: readRefresh(),
  setUser: (user) => set({ user }),
  setTokens: (t) => {
    writeRefresh(t.refreshToken);
    set({ accessToken: t.accessToken, refreshToken: t.refreshToken });
  },
  clear: () => {
    writeRefresh(null);
    set({ user: null, accessToken: null, refreshToken: null });
  },
}));
